import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import BackHomeButton from '../components/Common/BackHomeButton';
import { Button, FormControl, TableCard } from '../components/ui';
import { fetchIssuedLoans } from '../services/comersLoans';
import { formatCurrency, formatDate } from '../utils/numberFormatter';
import '../styles/requests.css';
import '../styles/requestStatusBadges.css';

const DEFAULT_PAGINATION = {
  total: 0,
  current_page: 1,
  per_page: 25,
  last_page: 1
};

const formatDateFilterLabel = (value) => {
  if (!value) return '';

  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
};

const IssuedLoansPage = () => {
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [filters, setFilters] = useState({
    search: '',
    status: 'active',
    date_from: '',
    date_to: ''
  });
  const [searchInput, setSearchInput] = useState('');

  const hasActiveFilters = Boolean(
    searchInput.trim() || filters.date_from || filters.date_to || (filters.status && filters.status !== 'active')
  );

  const filterSummary = useMemo(() => {
    const parts = [
      filters.status === 'active' ? 'Activos' : filters.status,
      filters.date_from ? `Desde ${formatDateFilterLabel(filters.date_from)}` : null,
      filters.date_to ? `Hasta ${formatDateFilterLabel(filters.date_to)}` : null,
      searchInput.trim() ? `"${searchInput.trim()}"` : null
    ].filter(Boolean);

    return parts.join(' · ');
  }, [filters.date_from, filters.date_to, filters.status, searchInput]);

  const loadLoans = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const searchValue = filters.search.length === 0 || filters.search.length >= 3
        ? filters.search
        : '';
      const result = await fetchIssuedLoans({
        ...filters,
        search: searchValue,
        page: pagination.current_page,
        per_page: pagination.per_page
      });

      setLoans(result.loans);
      setPagination((prev) => ({
        ...prev,
        ...result.pagination
      }));
    } catch (error) {
      console.error('Error al obtener créditos emitidos:', error);
      setLoans([]);
      setErrorMessage('No se pudieron cargar los créditos emitidos. Intentá nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.current_page, pagination.per_page]);

  useEffect(() => {
    loadLoans();
  }, [loadLoans]);

  useEffect(() => {
    const initialSearch = searchInput.length === 0 && !filters.search;
    if (!initialSearch && (searchInput.length === 0 || searchInput.length >= 3)) {
      setFilters(prev => ({
        ...prev,
        search: searchInput
      }));
      setPagination(prev => ({ ...prev, current_page: 1 }));
    }
  }, [filters.search, searchInput]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    if (name === 'search') {
      setSearchInput(value);
      return;
    }

    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      status: 'active',
      date_from: '',
      date_to: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleGoToCustomer = (dni) => {
    if (dni) {
      navigate(`/clientes/${dni}`);
    }
  };

  const TableSkeleton = () => (
    <>
      {Array.from({ length: pagination.per_page }).map((_, index) => (
        <tr key={index} className="requests-page__row-skeleton">
          <td data-label="Préstamo"><Skeleton width={90} height={16} /></td>
          <td data-label="Cliente"><Skeleton width={150} height={16} /></td>
          <td data-label="DNI"><Skeleton width={82} height={14} /></td>
          <td data-label="Otorgamiento"><Skeleton width={88} height={14} /></td>
          <td data-label="Capital"><Skeleton width={96} height={16} /></td>
          <td data-label="Cuotas"><Skeleton width={34} height={16} /></td>
          <td data-label="Valor cuota"><Skeleton width={96} height={16} /></td>
          <td data-label="Sucursal"><Skeleton width={116} height={14} /></td>
          <td data-label="Estado" className="text-center"><Skeleton width={88} height={28} borderRadius={999} /></td>
          <td data-label="Acciones"><Skeleton width={92} height={34} borderRadius={10} /></td>
        </tr>
      ))}
    </>
  );

  const Pagination = ({ disabled }) => (
    <nav aria-label="Navegación de créditos" className="requests-page__pagination-nav">
      <ul className="pagination justify-content-center mb-0">
        <li className={`page-item ${disabled || pagination.current_page === 1 ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
            disabled={disabled || pagination.current_page === 1}
          >
            Anterior
          </button>
        </li>
        {Array.from({ length: pagination.last_page }, (_, index) => (
          <li
            key={index}
            className={`page-item ${pagination.current_page === index + 1 ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
          >
            <button
              className="page-link"
              onClick={() => setPagination(prev => ({ ...prev, current_page: index + 1 }))}
              disabled={disabled}
            >
              {index + 1}
            </button>
          </li>
        ))}
        <li className={`page-item ${disabled || pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
            disabled={disabled || pagination.current_page === pagination.last_page}
          >
            Siguiente
          </button>
        </li>
      </ul>
    </nav>
  );

  return (
    <div className="page-content requests-page rg-shell-content">
      <header className="requests-page__heading">
        <div>
          <h1 className="requests-page__title">Créditos emitidos</h1>
          <p className="requests-page__lead">
            Consultá los créditos otorgados por tu comercio.
          </p>
        </div>
        <div className="requests-page__heading-actions">
          <BackHomeButton />
        </div>
      </header>

      <section className="section requests-page__section">
        <TableCard
          className="requests-page__table-card"
          bodyClassName="requests-page__table-card-body"
          toolbarClassName="requests-page__toolbar"
          tableClassName="requests-page__table-wrap"
          paginationClassName="requests-page__pagination"
          header={(
            <div className="requests-page__context-bar">
              <div className="requests-page__context-copy">
                <span className="requests-page__context-eyebrow">Créditos</span>
                <p className="requests-page__context-text">
                  {loading
                    ? 'Actualizando créditos...'
                    : `${pagination.total || 0} crédito${pagination.total === 1 ? '' : 's'} emitido${pagination.total === 1 ? '' : 's'}`}
                </p>
              </div>
              <div className="requests-page__context-actions">
                <span className="requests-page__context-pill">
                  {hasActiveFilters ? filterSummary : 'Activos'}
                </span>
              </div>
            </div>
          )}
          toolbar={
            <div className="requests-page__filters">
              <div className="row g-3">
                <div className="col-12 col-lg-4">
                  <FormControl
                    id="issued-loans-search"
                    name="search"
                    label="Búsqueda general"
                    placeholder="Cliente, DNI o préstamo"
                    value={searchInput}
                    onChange={handleFilterChange}
                    hint="La búsqueda se aplica cuando está vacía o con 3 caracteres en adelante."
                    iconLeft={<i className="bi bi-search" aria-hidden />}
                  />
                </div>
                <div className="col-12 col-sm-6 col-lg-2">
                  <FormControl
                    id="issued-loans-status"
                    as="select"
                    name="status"
                    label="Estado"
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <option value="active">Activos</option>
                  </FormControl>
                </div>
                <div className="col-12 col-sm-6 col-lg-2">
                  <FormControl
                    id="issued-loans-date-from"
                    type="date"
                    name="date_from"
                    label="Desde"
                    value={filters.date_from}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-12 col-sm-6 col-lg-2">
                  <FormControl
                    id="issued-loans-date-to"
                    type="date"
                    name="date_to"
                    label="Hasta"
                    value={filters.date_to}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-12 col-lg-2 requests-page__filters-actions-col">
                  <div className="requests-page__filters-actions">
                    <Button
                      variant="ghost"
                      className="requests-page__filter-button"
                      onClick={clearFilters}
                      disabled={!hasActiveFilters}
                    >
                      <i className="bi bi-eraser" aria-hidden />
                      Limpiar
                    </Button>
                    <Button
                      variant="outlineSecondary"
                      className="requests-page__filter-button"
                      onClick={loadLoans}
                      disabled={loading}
                    >
                      <i className={`bi bi-arrow-clockwise${loading ? ' rg-spin' : ''}`} aria-hidden />
                      Actualizar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          }
          table={
            <table className="table table-hover requests-page__table">
              <thead>
                <tr>
                  <th>Préstamo</th>
                  <th>Cliente</th>
                  <th>DNI</th>
                  <th>Otorgamiento</th>
                  <th>Capital</th>
                  <th>Cuotas</th>
                  <th>Valor cuota</th>
                  <th>Sucursal</th>
                  <th className="text-center">Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton />
                ) : errorMessage ? (
                  <tr>
                    <td colSpan="10" className="requests-page__empty-cell">
                      <i className="bi bi-exclamation-circle requests-page__empty-icon" aria-hidden />
                      <div className="requests-page__empty-title">No pudimos cargar los créditos</div>
                      <p className="requests-page__empty-copy">{errorMessage}</p>
                    </td>
                  </tr>
                ) : loans.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="requests-page__empty-cell">
                      <i className="bi bi-inbox requests-page__empty-icon" aria-hidden />
                      <div className="requests-page__empty-title">Todavía no hay créditos emitidos para este comercio.</div>
                      <p className="requests-page__empty-copy">
                        {hasActiveFilters
                          ? 'Probá ajustar los filtros para volver a ver resultados.'
                          : 'Los préstamos otorgados aparecerán acá cuando estén emitidos.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  loans.map((loan) => (
                    <tr key={loan.loan_uid || loan.loan_identifier}>
                      <td data-label="Préstamo">{loan.loan_identifier || loan.loan_uid || '—'}</td>
                      <td data-label="Cliente">{loan.customer?.name || '—'}</td>
                      <td data-label="DNI">{loan.customer?.dni || '—'}</td>
                      <td data-label="Otorgamiento">{formatDate(loan.granted_at) || '—'}</td>
                      <td data-label="Capital">{formatCurrency(loan.capital)}</td>
                      <td data-label="Cuotas" className="text-center">{loan.installments ?? '—'}</td>
                      <td data-label="Valor cuota">{loan.installment_amount != null ? formatCurrency(loan.installment_amount) : '—'}</td>
                      <td data-label="Sucursal">{loan.commerce?.branch_name || loan.commerce?.branch_id || '—'}</td>
                      <td data-label="Estado" className="text-center">
                        <span className="request-status-badge request-status-approved">
                          {loan.status_label || 'Activo'}
                        </span>
                      </td>
                      <td data-label="Acciones">
                        <div className="btn-group requests-page__actions">
                          <button
                            className="btn btn-sm btn-outline-primary requests-page__view-btn"
                            onClick={() => handleGoToCustomer(loan.customer?.dni)}
                            disabled={!loan.customer?.dni}
                            title="Ver cliente"
                          >
                            <i className="bi bi-person me-1"></i>
                            Ver cliente
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          }
          pagination={<Pagination disabled={loading || Boolean(errorMessage)} />}
        />
      </section>
    </div>
  );
};

export default IssuedLoansPage;
